import * as React from 'react';

import styled from '@mint-lib/styled';
import Dialog, { DialogProps as DialogPropsM } from '@mui/material/Dialog';

import AppBar from '../AppBar/AppBar.jsx';
import Box from '../Box/Box.jsx';
import Button, { ButtonProps } from '../Button/Button.jsx';
import DialogContent from '../DialogContent/DialogContent.jsx';
import IconButton from '../IconButton/IconButton.jsx';
import Typography from '../Typography/Typography.jsx';
import Transition from './DialogFullScreenTransition.jsx';

interface BtnProps extends ButtonProps {
  label?: string;
}

export interface DialogFullScreenProps extends DialogPropsM {
  /**
   * If `true`, the DialogFullScreen is open.
   */
  open: DialogPropsM['open'];

  /**
   * If true, hitting escape will not fire the onClose callback.
   */
  disableEscapeKeyDown?: boolean;

  /**
   */
  children?: React.ReactNode;

  /**
   * The component used to render the body of the dialog.
   */
  PaperComponent?: DialogPropsM['PaperComponent'];

  /**
   * Override or extend the styles applied to the component. See CSS API https://mui.com/api/dialog/#css for more details.
   */
  classes?: DialogPropsM['classes'];

  /**
   * Determine the container for scrolling the dialog.
   */
  scroll?: DialogPropsM['scroll'];
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   * See the `sx` page for more details. https://mui.com/system/the-sx-prop/
   */
  sx?: DialogPropsM['sx'];

  /**
   * Title of a dialog
   */
  title?: string;
  /**
   * Object with button props for submit button
   */
  submitButtonProps?: BtnProps | null;

  /**
   * Object with button props for submit button
   */
  cancelButtonProps?: BtnProps | null;

  /**
   * Callback to control modal closing
   */
  onClose: (...args: any) => void;
  /**
   * An array of object to render additional buttons
   */
  additionalActions?: BtnProps[] | null;
  /**
   * Pass your own submit button
   */
  submitButton?: React.ReactNode;
}

/**
 * @deprecated Use Dialog from @mint-lib/tw-components instead
 */
function DialogFullScreen({
  open,
  TransitionProps,
  title,
  onClose,
  submitButtonProps,
  cancelButtonProps,
  additionalActions,
  scroll = 'paper',
  submitButton,
  ...props
}: DialogFullScreenProps) {
  return (
    <StyledDialog
      open={open}
      fullScreen
      onClose={onClose}
      maxWidth={false}
      TransitionComponent={Transition}
      scroll={scroll}
      {...props}
    >
      <StyledAppBar>
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            icon="Close"
            size="small"
            variant="icon"
            color="secondary"
          />
          <Typography component="h3" variant="h3" color="text.primary">
            {title}
          </Typography>
        </Box>
        <Actions>
          <Box>
            {additionalActions?.map(({ label, ...rest }) => (
              <Button size="large" sx={{ marginRight: '0.5rem' }} {...rest}>
                {label}
              </Button>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            {!!cancelButtonProps && (
              <Button
                {...cancelButtonProps}
                variant={
                  cancelButtonProps?.variant
                    ? cancelButtonProps.variant
                    : 'text'
                }
                color={
                  cancelButtonProps?.color ? cancelButtonProps.color : 'primary'
                }
                size="large"
              >
                {cancelButtonProps?.label ? cancelButtonProps?.label : 'Cancel'}
              </Button>
            )}

            {submitButton ? (
              submitButton
            ) : (
              <Button
                {...submitButtonProps}
                variant={
                  submitButtonProps ? submitButtonProps.variant : 'contained'
                }
                color={submitButtonProps ? submitButtonProps.color : 'primary'}
                size="large"
              >
                {submitButtonProps?.label ? submitButtonProps?.label : 'Save'}
              </Button>
            )}
          </Box>
        </Actions>
      </StyledAppBar>
      <StyledDialogContent>{props.children}</StyledDialogContent>
    </StyledDialog>
  );
}

export default DialogFullScreen;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    box-shadow: none;
    background-image: none;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 1rem;
`;

const StyledAppBar = styled(AppBar)`
  position: relative;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: ${({ theme }) => theme.palette.ui.primary};
  background-image: none;
  box-shadow: none;
  border-bottom: 1px solid ${({ theme }) => theme.palette.ui.tertiary};
`;

const Actions = styled(Box)`
  display: flex;
  justify-content: space-between;
`;
